import { useLocation } from 'react-router-dom'
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'

import { BlockchainVerifiedBanner } from '../blockchain-verified-banner'
import { IUnSdg, IScan, IImpactConsumerScanItem, IImpact } from 'src/types'
import {
  Item,
  ItemBottom,
  ItemImage,
  ItemText,
  LinkSt,
  ItemTextWrapper,
} from './impact-cards-hor.styled'
import { AIGeneratedBanner } from '../ai-generated-banner'
import { AIVerifiedBanner } from '../ai-verified-banner'
import { WorldHeartIcon } from 'src/assets/icons/v2/WorldHeartIcon'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { stripProductHomeSegment } from 'src/utils/route.utils'
import { useCustomModal } from 'src/hooks'
import { CustomModalAnimated } from '../modal-animated'
import { AccountImpactDetails } from '../account-impacts/account-impact-details'
interface IProps {
  title: string | number
  subtitle: string
  image: string
  unSdg: Array<IUnSdg>
  id: string
  quantityName: string
  scans: IScan[]
  primaryColor: string
  walletAddress: string
  showWalletAddress: boolean
  showBcInfo: boolean
  accountImage: string
  isImpactsPage: boolean
  aiGenerated: boolean
  aiVerifiedCount: number
  aiVerifiedInfo: IImpactConsumerScanItem[]
  templateId?: string
  item?: IImpact
}

export const ImpactCard = ({
  templateId,
  image,
  title,
  subtitle,
  id,
  quantityName,
  scans,
  primaryColor,
  walletAddress,
  showWalletAddress,
  showBcInfo,
  accountImage,
  aiGenerated,
  aiVerifiedCount,
  aiVerifiedInfo,
  item,
}: IProps) => {
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { handleClose, handleOpen, open } = useCustomModal()

  const isEmpty = !image || image.trim().length === 0
  const isUiAvatar = image?.includes('ui-avatars.com/api')
  const showFallback = isEmpty || isUiAvatar
  const isFairtrade = templateId === JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE

  const baseProductPath = stripProductHomeSegment(location.pathname)

  const isOnImpactsRoute = /\/impacts(\/|#|$)/.test(location.pathname + location.hash)
  const openAsSlide = !isOnImpactsRoute

  const cardInner = (
    <>
      <ItemImage
        id="impact-card-component__components-impacts-cards-hor__itemimage__l70"
        style={
          showFallback
            ? {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: 'none',
              }
            : { backgroundImage: `url(${image})` }
        }
      >
        {showFallback && <WorldHeartIcon size={80} color={'#666'} />}
      </ItemImage>
      <ItemBottom
        id="impact-card-component__components-impacts-cards-hor__itembottom__l84"
        isQuantity={!!title}
      >
        {title && (
          <ItemTextWrapper id="impact-card-component__components-impacts-cards-hor__itemtextwrapper__l86">
            <ItemText
              id="impact-card-component__components-impacts-cards-hor__itemtext__l87"
              variant="h4"
            >
              {title}
            </ItemText>
            <ItemText
              id="impact-card-component__components-impacts-cards-hor__itemtext__l88"
              variant="h6"
            >
              {quantityName}
            </ItemText>
          </ItemTextWrapper>
        )}
        <ItemText
          id="impact-card-component__components-impacts-cards-hor__itemtext__l91"
          variant="body2"
        >
          {subtitle}
        </ItemText>
      </ItemBottom>
    </>
  )

  return (
    <Box
      id="impact-card-component__components-impacts-cards-hor__box__l60"
      sx={{ position: 'relative' }}
    >
      <Item
        id="impact-card-component__components-impacts-cards-hor__item__l61"
        disabled={false}
      >
        {openAsSlide ? (
          <Box
            onClick={handleOpen}
            sx={{
              cursor: 'pointer',
              paddingTop: aiGenerated ? '20px' : '0px',
              display: 'block',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            {cardInner}
          </Box>
        ) : (
          <LinkSt
            id="impact-card-component__components-impacts-cards-hor__linkst__l62"
            style={{
              paddingTop: aiGenerated ? '20px' : '0px',
            }}
            to={`${baseProductPath}/impacts#${id}`}
            isFairtrade={isFairtrade}
          >
            {cardInner}
          </LinkSt>
        )}
      </Item>
      {showBcInfo && scans && scans.length > 0 && (
        <BlockchainVerifiedBanner
          size={40}
          scans={scans}
          stroke={primaryColor}
          walletAddress={walletAddress}
          showWalletAddress={showWalletAddress}
          impactImage={image}
          impactName={subtitle}
          accountImage={accountImage}
        />
      )}
      {aiGenerated && aiVerifiedCount > 0 && aiVerifiedInfo && (
        <AIVerifiedBanner aiVerifiedCount={aiVerifiedCount} data={aiVerifiedInfo} />
      )}
      {aiGenerated && <AIGeneratedBanner />}

      {openAsSlide && item && (
        <CustomModalAnimated
          ariaLable={`modal-impact-details-${item.id}`}
          open={open}
          handleClose={handleClose}
        >
          <Box
            sx={{
              zIndex: 101,
              width: '100vw',
              height: '100dvh',
              maxHeight: '100dvh',
              backgroundColor: theme.palette.common.white,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                color: theme.palette.common.black,
                alignSelf: 'flex-end',
                m: isMobile ? '0px' : '5px',
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              sx={{
                width: '100%',
                height: '100%',
                maxHeight: '100dvh',
                overflowY: 'auto',
              }}
            >
              <AccountImpactDetails
                title={item.quantity ?? ''}
                quantityName={item.quantity_type_name || item.custom_quantity_type || ''}
                subtitle={item.name}
                image={item.image_medium || item.image}
                unSdg={item.un_sdg || []}
                media={item.media || []}
                mainMedia={item.mainMedia || []}
                description={item.description}
                likes={item.totalLikes}
                liked={item.liked}
                impactId={item.id}
                showUnSdg
                aiGenerated={item.ai_generated}
              />
            </Box>
          </Box>
        </CustomModalAnimated>
      )}
    </Box>
  )
}
